import logger from '@/util/logger';
import {
    brandRequestIdFor,
    isSharedNameplateName,
    lineNameFromAlias,
    planBrandAliasAddition,
    planBrandCreation,
    planBrandLineAddition,
    planBrandLineRemoval,
    type SharedNameplateTable,
    sharedNameplateEntries,
} from '@rock/brands';
import { BrandAliasDao, BrandDao, BrandRequestDao, createId, SharedNameplateDao } from '@rock/db-admin';
import type { Brand, BrandAlias, BrandAliasKey, BrandId, BrandLine, Timestamp, UserId } from '@rock/types';
import { isBrandRequestOpen } from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import { republishBrandSearchIndexSoon } from './republishBrandSearchIndex';

const log = logger('brandCatalogActions');
const brandDao = new BrandDao();
const brandAliasDao = new BrandAliasDao();
const brandRequestDao = new BrandRequestDao();
const sharedNameplateDao = new SharedNameplateDao();

/**
 * The two catalog writes admin makes by name — Add Brand and add-alias — as one body each, shared by the
 * Brands pages and the brand requests queue. Each resolves against the alias projection first (the
 * authoritative "does this name already resolve?" read), applies the `@rock/brands` plan, and then
 * settles any pending request for the name so the models and recalls waiting on it fan out.
 */

export type CreateBrandOutcome =
    | { status: 'created'; brand: Brand }
    /** The name already resolves — to this brand (merged brands are followed to their winner). */
    | { status: 'exists'; brand: Brand }
    | { status: 'refused'; reason: string };

export async function createBrandFromName(name: string, by: UserId, displayName?: string): Promise<CreateBrandOutcome> {
    const now = stamp();
    const sharedNameplates = await currentTable();
    const existing = await resolveBrandName(name);
    if (existing.status === 'denied') return { status: 'refused', reason: existing.reason };
    if (existing.status === 'brand') {
        const isCanonical = normalizeBrandName(name).key === existing.brand.canonicalKey;
        const decision = existing.lineId
            ? { kind: 'line' as const, brandId: existing.brand.id, lineId: existing.lineId }
            : { kind: isCanonical ? ('brand' as const) : ('alias' as const), brandId: existing.brand.id };
        await resolvePendingRequestForName(name, decision, by);
        return { status: 'exists', brand: existing.brand };
    }
    const plan = planBrandCreation(name, { brandId: createId<BrandId>(), now, sharedNameplates, displayName });
    if (!plan) return { status: 'refused', reason: refusalReason(name, sharedNameplates) };

    await brandDao.applyBrandCreation(plan);
    log.info('Created brand from admin', { brandId: plan.brand.id, canonicalKey: plan.brand.canonicalKey });
    await resolvePendingRequestForName(name, { kind: 'brand', brandId: plan.brand.id }, by);
    return { status: 'created', brand: plan.brand };
}

export type AddAliasOutcome =
    | { status: 'added'; display: string }
    | { status: 'already-canonical' }
    /** The name resolves to a different brand; merge that one into this to take its names over. */
    | { status: 'taken'; brand: Brand }
    | { status: 'refused'; reason: string };

export async function addBrandAlias(brand: Brand, aliasName: string, by: UserId): Promise<AddAliasOutcome> {
    const now = stamp();
    const sharedNameplates = await currentTable();
    if (brand.mergedInto)
        return { status: 'refused', reason: 'This brand was merged; add the alias on the surviving brand.' };
    const existing = await resolveBrandName(aliasName);
    if (existing.status === 'denied') return { status: 'refused', reason: existing.reason };
    if (existing.status === 'brand' && existing.brand.id !== brand.id)
        return { status: 'taken', brand: existing.brand };
    if (existing.status === 'brand' && existing.lineId) {
        const line = brand.lines?.find((candidate) => candidate.id === existing.lineId);
        return {
            status: 'refused',
            reason: `“${aliasName}” already names the ${line?.displayName ?? existing.lineId} line.`,
        };
    }

    const plan = planBrandAliasAddition(brand, aliasName, now, sharedNameplates);
    if (!plan) return { status: 'refused', reason: refusalReason(aliasName, sharedNameplates) };
    const kind = plan.key === brand.canonicalKey ? 'brand' : 'alias';
    if (kind === 'brand' && brand.aliases.includes(plan.display)) {
        await restoreMissingAliasDocs(brand, plan.aliasDocs, now);
        await resolvePendingRequestForName(aliasName, { kind, brandId: brand.id }, by);
        return { status: 'already-canonical' };
    }

    await brandDao.applyBrandAliasAddition(brand.id, plan, now);
    log.info('Added brand alias', { brandId: brand.id, key: plan.key });
    await resolvePendingRequestForName(aliasName, { kind, brandId: brand.id }, by);
    return { status: 'added', display: plan.display };
}

/**
 * The brand's own name, re-entered: write back each of its lookup keys that resolves nowhere — no doc, or
 * one pointing at a junk or missing brand (free to claim, as `resolveBrandName` reads it). A deny
 * placeholder or a key another live brand owns is someone's decision and stays.
 */
async function restoreMissingAliasDocs(brand: Brand, aliasDocs: BrandAlias[], now: Timestamp): Promise<void> {
    const claimable = await Promise.all(aliasDocs.map((doc) => isClaimableKey(doc.id, brand.id)));
    const missing = aliasDocs.filter((_, index) => claimable[index]);
    if (missing.length === 0) return;
    await brandDao.applyBrandAliasAddition(brand.id, { aliasDocs: missing, brandAliases: brand.aliases }, now);
    log.info('Restored missing brand alias docs', { brandId: brand.id, keys: missing.map((doc) => doc.id) });
}

async function isClaimableKey(key: BrandAliasKey, brandId: BrandId): Promise<boolean> {
    const alias = await brandAliasDao.getBrandAlias(key);
    if (!alias) return true;
    if (alias.denied || alias.brandId === brandId) return false;
    return !alias.brandId || !(await brandDao.getLiveBrand(alias.brandId));
}

export type BrandNameResolution =
    | { status: 'brand'; brand: Brand; lineId?: string }
    | { status: 'denied'; reason: string }
    | { status: 'free' };

/**
 * What the alias projection says about a name (with the line it names, if any): a merged brand followed
 * to its winner, and a junk brand's key free to claim — the same rule every server resolver applies.
 */
export async function resolveBrandName(name: string): Promise<BrandNameResolution> {
    const key = normalizeBrandName(name).key as BrandAliasKey;
    if (!key) return { status: 'free' };
    const alias = await brandAliasDao.getBrandAlias(key);
    if (alias?.denied) {
        return {
            status: 'denied',
            reason: alias.reason ?? `“${name}” is a shared nameplate; use the qualified brands.`,
        };
    }
    if (!alias?.brandId) return { status: 'free' };
    const brand = await brandDao.getLiveBrand(alias.brandId);
    if (!brand) return { status: 'free' };
    const lineId = alias.lineId && brand.lines?.some((line) => line.id === alias.lineId) ? alias.lineId : undefined;
    return { status: 'brand', brand, ...(lineId ? { lineId } : {}) };
}

/**
 * A direct catalog edit settles a name without visiting the queue; if that name has an open request
 * (pending or parked), resolve it too, so the models and recalls waiting on it fan out all the same.
 */
async function resolvePendingRequestForName(
    name: string,
    decision: { kind: 'brand' | 'alias' | 'line'; brandId: BrandId; lineId?: string },
    by: UserId,
): Promise<void> {
    const requestId = brandRequestIdFor(name);
    if (!requestId) return;
    const request = await brandRequestDao.getBrandRequest(requestId);
    if (!request || !isBrandRequestOpen(request)) return;
    const now = stamp();
    await brandRequestDao.resolveBrandRequest(requestId, { ...decision, by, at: now }, now);
    log.info('Resolved pending brand request from a catalog edit', { requestId, ...decision });
}

export type AddLineOutcome =
    | { status: 'added'; line: BrandLine }
    /** `existingLine` when the brand already has the line and nothing new would be minted for it. */
    | { status: 'refused'; reason: string; existingLine?: BrandLine };

export interface AddLineInput {
    displayName: string;
    /** Spellings that should resolve to the line too (a request's name, a merged-away brand's), beyond "{brand name} {line}". */
    extraNames?: string[];
    /** An alias of the brand that was really naming the line: it leaves the alias list and its keys join the line. */
    absorbAlias?: string;
}

/**
 * Add a product line to a brand (or more spellings of one it has). Every key the plan would mint is
 * checked against the projection first — a key another brand owns is a merge, not a line — then the
 * plan lands in one DAO write and the pending request for each extra spelling settles as `line`. The
 * index then republishes in the background, so the app's typeahead shows the line now.
 */
export async function addBrandLine(brand: Brand, input: AddLineInput, by: UserId): Promise<AddLineOutcome> {
    const now = stamp();
    if (brand.mergedInto)
        return { status: 'refused', reason: 'This brand was merged; add the line on the surviving brand.' };
    const outcome = planBrandLineAddition(brand, { ...input, now }, await currentTable());
    if ('refusal' in outcome) {
        return {
            status: 'refused',
            reason: outcome.refusal,
            ...(outcome.existingLine ? { existingLine: outcome.existingLine } : {}),
        };
    }
    const taken = await firstKeyOwnedElsewhere(
        brand,
        outcome.plan.aliasDocs.map((doc) => doc.id),
    );
    if (taken) return { status: 'refused', reason: taken };

    await brandDao.applyBrandLineAddition(brand.id, outcome.plan, now);
    log.info('Added brand line', {
        brandId: brand.id,
        lineId: outcome.plan.line.id,
        keys: outcome.plan.aliasDocs.length,
    });
    for (const name of input.extraNames ?? []) {
        await resolvePendingRequestForName(name, { kind: 'line', brandId: brand.id, lineId: outcome.plan.line.id }, by);
    }
    republishBrandSearchIndexSoon();
    return { status: 'added', line: outcome.plan.line };
}

export async function removeBrandLine(brand: Brand, line: BrandLine): Promise<boolean> {
    const now = stamp();
    const plan = planBrandLineRemoval(brand, line.id, await brandAliasDao.findAliasesByBrandId(brand.id));
    if (!plan) return false;
    await brandDao.applyBrandLineRemoval(brand.id, plan, now);
    log.info('Removed brand line', { brandId: brand.id, lineId: line.id, deleted: plan.deleteKeys.length });
    republishBrandSearchIndexSoon();
    return true;
}

/**
 * "HUNTER BRUNSWICK" listed as an alias of Hunter Fan is really its Brunswick line: one plan absorbs
 * the alias (it leaves the list, its keys are re-minted WITH the line) — no window where the name
 * resolves to nothing. Refuses an alias that doesn't start with one of the brand's names.
 */
export async function convertAliasToLine(brand: Brand, alias: string, by: UserId): Promise<AddLineOutcome> {
    const lineName = lineNameFromAlias(brand, alias, await currentTable());
    if (!lineName) return { status: 'refused', reason: `“${alias}” doesn’t start with one of the brand’s names.` };
    return addBrandLine(brand, { displayName: lineName, absorbAlias: alias }, by);
}

/** The refusal for the first planned key the projection already gives to another brand (or denies). */
async function firstKeyOwnedElsewhere(brand: Brand, keys: BrandAliasKey[]): Promise<string | undefined> {
    const docs = await Promise.all(keys.map(async (key) => ({ key, doc: await brandAliasDao.getBrandAlias(key) })));
    for (const { key, doc } of docs) {
        if (doc?.denied) return `“${key}” is a shared nameplate; a line can’t take it.`;
        if (!doc?.brandId || doc.brandId === brand.id) continue;
        const owner = await brandDao.getLiveBrand(doc.brandId);
        if (owner && owner.id !== brand.id) {
            return `“${key}” already resolves to ${owner.canonicalName}; merge that brand into this one as the line instead.`;
        }
    }
    return undefined;
}

/**
 * The table as it stands at write time, read here rather than handed in from a page's loaded copy: the
 * deny docs cover whole-string OCR variants only, so a partial misread ("FR1GIDAIRE") is caught by the
 * planner's confusion-class gate alone — and a page that rendered before the table loaded would hand
 * the planner an empty one.
 */
async function currentTable(): Promise<SharedNameplateTable> {
    return sharedNameplateEntries(await sharedNameplateDao.findAllSharedNameplates());
}

function refusalReason(name: string, sharedNameplates: SharedNameplateTable): string {
    return isSharedNameplateName(name, sharedNameplates)
        ? `“${name}” is a shared nameplate; use the qualified brands.`
        : 'Placeholder or empty brand names are not allowed.';
}

function stamp(): Timestamp {
    return new Date().toISOString() as Timestamp;
}
